"use client"

import type React from "react"
import { useEffect, useRef, useCallback } from "react"
import type { Enemy, StatusEffect } from "@/types/enemy.types"
import type { Tower } from "@/types/tower.types"
import type { Hero, ActiveSkillEffect } from "@/types/hero.types"
import type { Projectile } from "@/types/projectile.types"
import type { GameState, LevelConfig, GameStats, ParticleEffect, EntanglingRootsEffect, Corpse, Position, AoeVisualEffect, PendingAoeWave, LandedPod, LaserBeamEffect, SoldierSpawnPortrait, ItemDropMessage } from "@/types/game.types"
import type { SummonedUnit } from "@/types/summon.types"
import type { BarracksSoldier } from "@/types/barracks.types"
import type { HeroInventory, ActiveItemEffect, DroppedItem } from "@/types/item.types"
import type { UpgradeState } from "@/types/upgrade.types"
import { getUpgradeModifiers } from "@/systems/upgrade-system"
import { type WaveSpawner, updateWaveSpawner, isWaveComplete } from "@/systems/wave-system"
import {
  getActiveShrineAuras,
  getStrongestShrineAtPosition,
  calculateShrineBuffs,
  getEmptyShrineBuffs,
} from "@/systems/shrine-system"
import {
  updateEnemy,
  shouldEnemyAttack,
  performEnemyAttack,
  damageEnemy,
  isEnemyDead,
  hasEnemyReachedBase,
  updateEnemyStatusEffects,
  calculateEnemyDamage,
  getCorpseFacingDirection,
  createEnemy,
  // Elite ability functions
  hasEliteAbility,
  hasEliteAbilityEffect,
  isEnemyPhaseShifted,
  triggerPhaseShift,
  updatePhaseShift,
  updateEliteAbilityCooldown,
  isPeriodicAbilityReady,
  resetPeriodicAbilityCooldown,
  hasLifesteal,
  applyLifesteal,
  hasGoldSteal,
  getGoldStealAmount,
  hasLycanthropy,
  createWerewolfFromSoldier,
  getSlowAttackerEffect,
  applyEliteDamageReduction,
  getInfernalAuraDamageMultiplier,
  applyPassiveRegeneration,
} from "@/systems/enemy-system"
import {
  updateSummonedWolf,
  updateSummonedTower,
  damageSummon,
  isSummonDead,
} from "@/systems/summon-system"
import { updateTower, canTowerFire, fireTower, updateArcherState, triggerArcherAttack, updateMageState, triggerMageAttack, updateSiegeState, triggerSiegeAttack, damageTower, isTowerDestroyed } from "@/systems/tower-system"
import {
  updateHero,
  updateHeroDash,
  canHeroAttack,
  performHeroAttack,
  damageHero,
  grantExperience,
  calculateHeroDamage,
} from "@/systems/hero-system"
import {
  createProjectilePooled,
  createArcProjectile,
  createSiegeArcProjectile,
  createRangerArcProjectile,
  updateProjectileInPlace,
  updateArcProjectileInPlace,
  updateSiegeProjectileTrail,
  isProjectileExpired,
  checkProjectileHitWithGrid,
  releaseProjectile,
} from "@/systems/projectile-system"
import { createFireGroundEffect, updateFireGroundEffects } from "@/systems/fire-effect-system"
import {
  updateBarracksSoldier,
  damageBarracksSoldier,
  handleSoldierDeath,
  checkSoldierRespawns,
  createBarracksSoldier,
  applySoldierAttackSpeedDebuff,
  updateSoldierDebuffs,
} from "@/systems/barracks-system"
import { calculateDamage } from "@/systems/damage-system"
import type { FireGroundEffect } from "@/types/game.types"
import { createSpatialGrid, rebuildGrid, type SpatialGrid } from "@/utils/spatial-grid"
import { enemyTypes } from "@/data/enemy-types"
import { generateId } from "@/utils/id-generator"
import { WORLD_WIDTH, WORLD_HEIGHT } from "@/data/game-settings"
import {
  updateSkillCooldowns,
  updateSkillEffect,
  isSkillEffectExpired,
  regenerateMana,
  updateEntanglingRootsEffect,
  isEntanglingRootsExpired,
  applyHeroDamageWithPassives,
  updateActiveBuffs,
  checkHealOnHit,
  applyAoeWaveDamage,
} from "@/systems/skill-system"
import { updateItemCooldowns, updateActiveItemEffects, getEquipmentDamageBonus, getEquipmentArmorBonus, getLifestealPercent, getEffectiveMaxHealth, getEquipmentHpRegen, getEquipmentManaRegen, getBeaconCharges, decrementBeaconCharges, getSpellPowerBonus, getEquipmentGoldBonus, getDamageReflectPercent } from "@/systems/item-system"
import { generateEnemyLoot, RARITY_COLORS } from "@/systems/loot-system"
import { popLatestClone, restoreHeroFromClone } from "@/services/hero-clone-service"
import { playTowerFireSound, playTowerHitSound, playDefeatSound, playSiegesmithTurretAttackSound, playSingleCoinSound, playPaladinVanguardRushHitSound, playLevelUpSound, playCloneAwakenSound, playEnemyFinishLineSound, playRangerAttackAimSound, playRangerAttackReleaseSound, playBuildingHitSound, playItemDropSound } from "@/hooks/use-sound-effects"
import { gameNow, freezeGameClock, unfreezeGameClock, getGameClockRate } from "@/utils/game-clock"

// Droppod healing aura constants
const DROPPOD_HEAL_RADIUS = 80 // pixels
const DROPPOD_HEAL_PERCENT = 0.01 // 1% of max health per second
const DROPPOD_HEAL_COOLDOWN_FRAMES = 60 // 1 second after taking damage before healing resumes
const DROPPOD_HEAL_PARTICLE_COOLDOWN = 90 // Spawn particle every 1.5 seconds while healing

// Get the highest damage reduction from active buffs (Empower Armor)
const getActiveDamageReduction = (hero: Hero): number => {
  if (!hero.activeBuffs || hero.activeBuffs.length === 0) return 0
  return hero.activeBuffs.reduce((max, buff) =>
    Math.max(max, buff.damageReduction || 0), 0)
}

interface UseGameLoopProps {
  gameState: GameState
  enemies: Enemy[]
  towers: Tower[]
  hero: Hero | null
  projectiles: Projectile[]
  skillEffects: ActiveSkillEffect[]
  summonedUnits: SummonedUnit[]
  rootsEffects: EntanglingRootsEffect[]
  fireEffects: FireGroundEffect[]
  corpses: Corpse[]
  heroInventory: HeroInventory
  activeItemEffects: ActiveItemEffect[]
  aoeVisualEffects: AoeVisualEffect[]
  pendingAoeWaves: PendingAoeWave[]
  laserBeamEffects: LaserBeamEffect[]
  barracksSoldiers: BarracksSoldier[]
  soldierSpawnPortraits: SoldierSpawnPortrait[]
  particleEffects?: ParticleEffect[]
  stats: GameStats
  level: LevelConfig
  waveSpawner: WaveSpawner
  waveSpawnerRef: React.MutableRefObject<WaveSpawner>
  clearEnemiesRef: React.MutableRefObject<boolean>
  currentWaveIndex: number
  landedPod: LandedPod | null
  isDroppodSelected?: boolean
  upgradeState: UpgradeState
  heroismLevel: number
  onEnemySpawn?: (enemy: Enemy) => void
  setters: {
    setEnemies: React.Dispatch<React.SetStateAction<Enemy[]>>
    setTowers: React.Dispatch<React.SetStateAction<Tower[]>>
    setHero: React.Dispatch<React.SetStateAction<Hero | null>>
    setProjectiles: React.Dispatch<React.SetStateAction<Projectile[]>>
    setSkillEffects: React.Dispatch<React.SetStateAction<ActiveSkillEffect[]>>
    setSummonedUnits: React.Dispatch<React.SetStateAction<SummonedUnit[]>>
    setRootsEffects: React.Dispatch<React.SetStateAction<EntanglingRootsEffect[]>>
    setFireEffects: React.Dispatch<React.SetStateAction<FireGroundEffect[]>>
    setCorpses: React.Dispatch<React.SetStateAction<Corpse[]>>
    setStats: React.Dispatch<React.SetStateAction<GameStats>>
    setGameState: React.Dispatch<React.SetStateAction<GameState>>
    setShowNextWaveButton: React.Dispatch<React.SetStateAction<boolean>>
    setWaveStartTime: React.Dispatch<React.SetStateAction<number | null>>
    setParticleEffects: React.Dispatch<React.SetStateAction<ParticleEffect[]>>
    setHeroInventory: React.Dispatch<React.SetStateAction<HeroInventory>>
    setActiveItemEffects: React.Dispatch<React.SetStateAction<ActiveItemEffect[]>>
    setAoeVisualEffects: React.Dispatch<React.SetStateAction<AoeVisualEffect[]>>
    setPendingAoeWaves: React.Dispatch<React.SetStateAction<PendingAoeWave[]>>
    setLaserBeamEffects: React.Dispatch<React.SetStateAction<LaserBeamEffect[]>>
    setBarracksSoldiers: React.Dispatch<React.SetStateAction<BarracksSoldier[]>>
    setSoldierSpawnPortraits: React.Dispatch<React.SetStateAction<SoldierSpawnPortrait[]>>
    setDroppedItems: React.Dispatch<React.SetStateAction<DroppedItem[]>>
    setItemDropMessages: React.Dispatch<React.SetStateAction<ItemDropMessage[]>>
    triggerCloneRestoreEffect: (restoredLevel: number, heroPosition?: Position) => void
    setIsLastWaveComplete: React.Dispatch<React.SetStateAction<boolean>>
  }
}

export function useGameLoop({
  gameState,
  enemies,
  towers,
  hero,
  projectiles,
  skillEffects,
  summonedUnits,
  rootsEffects,
  fireEffects,
  corpses,
  heroInventory,
  activeItemEffects,
  aoeVisualEffects,
  pendingAoeWaves,
  laserBeamEffects,
  barracksSoldiers,
  soldierSpawnPortraits,
  stats,
  level,
  waveSpawnerRef,
  clearEnemiesRef,
  currentWaveIndex,
  landedPod,
  isDroppodSelected = false,
  upgradeState,
  heroismLevel,
  onEnemySpawn,
  setters,
}: UseGameLoopProps) {
  const animationFrameRef = useRef<number>()
  const frameCountRef = useRef(0)

  // Spatial grid for efficient collision detection
  const enemyGridRef = useRef<SpatialGrid<Enemy>>(createSpatialGrid<Enemy>())

  // Use refs for game state to avoid stale closures
  const towersRef = useRef<Tower[]>(towers)
  const enemiesRef = useRef<Enemy[]>(enemies)
  const heroRef = useRef<Hero | null>(hero)
  const projectilesRef = useRef<Projectile[]>(projectiles)
  const skillEffectsRef = useRef<ActiveSkillEffect[]>(skillEffects)
  const summonedUnitsRef = useRef<SummonedUnit[]>(summonedUnits)
  const rootsEffectsRef = useRef<EntanglingRootsEffect[]>(rootsEffects)
  const fireEffectsRef = useRef<FireGroundEffect[]>(fireEffects)
  const corpsesRef = useRef<Corpse[]>(corpses)
  const statsRef = useRef<GameStats>(stats)
  const heroInventoryRef = useRef<HeroInventory>(heroInventory)
  const activeItemEffectsRef = useRef<ActiveItemEffect[]>(activeItemEffects)
  const aoeVisualEffectsRef = useRef<AoeVisualEffect[]>(aoeVisualEffects)
  const pendingAoeWavesRef = useRef<PendingAoeWave[]>(pendingAoeWaves)
  const laserBeamEffectsRef = useRef<LaserBeamEffect[]>(laserBeamEffects)
  const barracksSoldiersRef = useRef<BarracksSoldier[]>(barracksSoldiers)
  const soldierSpawnPortraitsRef = useRef<SoldierSpawnPortrait[]>(soldierSpawnPortraits)
  const landedPodRef = useRef<LandedPod | null>(landedPod)
  const isDroppodSelectedRef = useRef<boolean>(isDroppodSelected)
  const droppodHealAccumulatorRef = useRef<number>(0) // Accumulate heal amount until >= 1
  const droppodParticleCooldownRef = useRef<number>(0) // Cooldown for heal particle spawning

  // Keep refs in sync with props
  useEffect(() => {
    towersRef.current = towers
  }, [towers])
  useEffect(() => {
    enemiesRef.current = enemies
  }, [enemies])
  useEffect(() => {
    heroRef.current = hero
  }, [hero])
  useEffect(() => {
    projectilesRef.current = projectiles
  }, [projectiles])
  useEffect(() => {
    skillEffectsRef.current = skillEffects
  }, [skillEffects])
  useEffect(() => {
    summonedUnitsRef.current = summonedUnits
  }, [summonedUnits])
  useEffect(() => {
    rootsEffectsRef.current = rootsEffects
  }, [rootsEffects])
  useEffect(() => {
    fireEffectsRef.current = fireEffects
  }, [fireEffects])
  useEffect(() => {
    corpsesRef.current = corpses
  }, [corpses])
  useEffect(() => {
    statsRef.current = stats
  }, [stats])
  useEffect(() => {
    heroInventoryRef.current = heroInventory
  }, [heroInventory])
  useEffect(() => {
    activeItemEffectsRef.current = activeItemEffects
  }, [activeItemEffects])
  useEffect(() => {
    aoeVisualEffectsRef.current = aoeVisualEffects
  }, [aoeVisualEffects])
  useEffect(() => {
    pendingAoeWavesRef.current = pendingAoeWaves
  }, [pendingAoeWaves])
  useEffect(() => {
    laserBeamEffectsRef.current = laserBeamEffects
  }, [laserBeamEffects])
  useEffect(() => {
    barracksSoldiersRef.current = barracksSoldiers
  }, [barracksSoldiers])
  useEffect(() => {
    soldierSpawnPortraitsRef.current = soldierSpawnPortraits
  }, [soldierSpawnPortraits])
  useEffect(() => {
    landedPodRef.current = landedPod
  }, [landedPod])
  useEffect(() => {
    isDroppodSelectedRef.current = isDroppodSelected
  }, [isDroppodSelected])
  // Refs for props read inside the loop body - keeps runGameLoop's identity
  // stable (so the RAF effect doesn't tear down) without stale closures
  const upgradeStateRef = useRef<UpgradeState>(upgradeState)
  const heroismLevelRef = useRef<number>(heroismLevel)
  const onEnemySpawnRef = useRef(onEnemySpawn)
  useEffect(() => {
    upgradeStateRef.current = upgradeState
  }, [upgradeState])
  useEffect(() => {
    heroismLevelRef.current = heroismLevel
  }, [heroismLevel])
  useEffect(() => {
    onEnemySpawnRef.current = onEnemySpawn
  }, [onEnemySpawn])

  // Helper function to attempt hero restoration from clone when hero dies
  const tryRestoreFromClone = useCallback((
    currentInventory: HeroInventory
  ): { success: boolean; hero?: Hero; inventory?: HeroInventory } => {
    // Check if we have beacon charges
    const charges = getBeaconCharges(currentInventory)
    if (charges <= 0) {
      return { success: false }
    }

    // Pop the most recent clone
    const clone = popLatestClone()
    if (!clone) {
      return { success: false }
    }

    // Calculate spawn position from landed pod
    const pod = landedPodRef.current
    if (!pod || pod.tileX === undefined || pod.tileY === undefined) {
      return { success: false }
    }

    // Spawn next to the pod (same logic as initial spawn)
    const tileSize = 32 // gameSettings.canvas.tileSize
    const tryLeft = Math.random() > 0.5
    const heroTileX = pod.tileX + (tryLeft ? -1 : 1)
    const heroTileY = pod.tileY
    const spawnPosition = {
      x: heroTileX * tileSize + tileSize / 2,
      y: heroTileY * tileSize + tileSize / 2,
    }

    // Restore hero from clone snapshot at full health
    const restoredHero = restoreHeroFromClone(clone, spawnPosition)
    playCloneAwakenSound()

    // Decrement beacon charges
    const decrementedInventory = decrementBeaconCharges(currentInventory)

    // Restore inventory from clone snapshot (except beacon which keeps current charges)
    const restoredInventory: HeroInventory = {
      slots: clone.inventory.slots.map((slot, i) =>
        i === 0 ? decrementedInventory.slots[0] : slot
      )
    }

    return { success: true, hero: restoredHero, inventory: restoredInventory }
  }, [])

  const runGameLoop = useCallback(() => {
    frameCountRef.current++

    // Check if we need to clear enemies (e.g., when calling wave early)
    if (clearEnemiesRef.current) {
      enemiesRef.current = []
      clearEnemiesRef.current = false
    }

    // Clone current state from refs
    let updatedEnemies = [...enemiesRef.current]
    let updatedTowers = [...towersRef.current]
    let updatedHero = heroRef.current ? { ...heroRef.current } : null
    let updatedProjectiles = [...projectilesRef.current]
    let updatedSkillEffects = [...skillEffectsRef.current]
    let updatedSummons = [...summonedUnitsRef.current]
    let updatedRootsEffects = [...rootsEffectsRef.current]
    let updatedCorpses = [...corpsesRef.current]
    let updatedLaserBeams = [...laserBeamEffectsRef.current]
    let updatedBarracksSoldiers = [...barracksSoldiersRef.current]
    const updatedStats = { ...statsRef.current }
    let updatedInventory = heroInventoryRef.current
    let updatedItemEffects = [...activeItemEffectsRef.current]

    // === SPAWN ENEMIES ===
    waveSpawnerRef.current = updateWaveSpawner(waveSpawnerRef.current, (newEnemy) => {
      updatedEnemies.push(newEnemy)
      // Notify about new enemy for UI notifications
      onEnemySpawnRef.current?.(newEnemy)
    })

    // === UPDATE ENEMIES ===
    updatedEnemies = updatedEnemies.map((enemy) => updateEnemy(enemy, level.path, updatedHero, updatedTowers, updatedSummons, updatedBarracksSoldiers))

    // Build spatial grid and enemy lookup map for efficient collision and targeting
    rebuildGrid(enemyGridRef.current, updatedEnemies)
    const enemyMap = new Map(updatedEnemies.map((e) => [e.id, e]))

    // === UPDATE SHRINE BUFFS ===
    // Calculate shrine auras once per frame
    const shrineAuras = getActiveShrineAuras(updatedTowers)
    const heroismBuffs = heroismLevelRef.current > 0 ? calculateShrineBuffs(heroismLevelRef.current) : null

    // Update hero shrine buffs
    if (updatedHero) {
      const strongestShrine = getStrongestShrineAtPosition(updatedHero.position, shrineAuras)
      updatedHero = {
        ...updatedHero,
        shrineBuffs:
          strongestShrine && heroismBuffs
            ? { ...heroismBuffs, activeAuraShrineId: strongestShrine.shrineId }
            : getEmptyShrineBuffs(),
      }
    }

    // Update summon shrine buffs
    updatedSummons = updatedSummons.map((summon) => {
      const strongestShrine = getStrongestShrineAtPosition(summon.position, shrineAuras)
      return {
        ...summon,
        shrineBuffs:
          strongestShrine && heroismBuffs
            ? { ...heroismBuffs, activeAuraShrineId: strongestShrine.shrineId }
            : getEmptyShrineBuffs(),
      }
    })

    // Update barracks soldier shrine buffs
    updatedBarracksSoldiers = updatedBarracksSoldiers.map((soldier) => {
      const strongestShrine = getStrongestShrineAtPosition(soldier.position, shrineAuras)
      return {
        ...soldier,
        shrineBuffs:
          strongestShrine && heroismBuffs
            ? { ...heroismBuffs, activeAuraShrineId: strongestShrine.shrineId }
            : getEmptyShrineBuffs(),
      }
    })

    // === UPDATE HERO ===
    if (updatedHero) {
      updatedHero = updateHero(updatedHero, updatedEnemies, 1, updatedInventory) // 1 frame, pass inventory for equipment speed bonus
      updatedHero = updateSkillCooldowns(updatedHero, 1, updatedInventory)
      updatedHero = updateActiveBuffs(updatedHero, 1) // Update active buff durations

      // Clear expired slow effect
      if (frameCountRef.current >= updatedHero.slowEndFrame && updatedHero.slowMultiplier !== 1) {
        updatedHero = {
          ...updatedHero,
          slowMultiplier: 1,
          slowEndFrame: 0,
        }
      }

      // Health and mana regeneration based on level (only if alive)
      // Base regen: 0.1 health/frame (6 HP/s), +0.05 per level (+3 HP/s per level)
      // Mana regen: 0.1 mana/frame (6 MP/s), +0.03 per level (+1.8 MP/s per level)
      // Equipment hp-regen / mana-regen are authored per second (the codex reads
      // "+15 health regeneration"), so they are divided by 60 to join these
      // per-frame rates.
      if (updatedHero.health > 0) {
        const healthRegenRate =
          0.1 + updatedHero.level * 0.05 + getEquipmentHpRegen(updatedInventory) / 60
        const manaRegenRate =
          0.1 + updatedHero.level * 0.03 + getEquipmentManaRegen(updatedInventory) / 60
        // Calculate effective max health for regen cap (base + equipment bonuses)
        const regenMaxHealth = getEffectiveMaxHealth(updatedHero, updatedInventory)

        updatedHero = {
          ...updatedHero,
          health: Math.min(regenMaxHealth, updatedHero.health + healthRegenRate),
        }
        updatedHero = regenerateMana(updatedHero, 1, manaRegenRate)

        // Forcefield regeneration (Rift Blade only) - 5x health regen rate
        // Requires 3 seconds (180 frames) since last forcefield damage before recharging
        if (updatedHero.type === "paladin" && updatedHero.maxForcefield > 0 && updatedHero.forcefield < updatedHero.maxForcefield) {
          const framesSinceLastForcefieldDamage = updatedHero.lastForcefieldDamageFrame
            ? frameCountRef.current - updatedHero.lastForcefieldDamageFrame
            : 181 // Never damaged = can regen

          // Only regenerate after 3 seconds (180 frames at 60fps)
          if (framesSinceLastForcefieldDamage > 180) {
            const forcefieldRegenRate = healthRegenRate * 5 // 5x health regen
            updatedHero = {
              ...updatedHero,
              forcefield: Math.min(updatedHero.maxForcefield, updatedHero.forcefield + forcefieldRegenRate),
            }
          }
        }

        // === DROPPOD HEALING AURA ===
        // Heal hero if: in range, not at full health, not recently damaged, droppod not selected
        const pod = landedPodRef.current
        // Calculate effective max health (base + equipment bonuses)
        const effectiveMaxHealth = getEffectiveMaxHealth(updatedHero, updatedInventory)
        // Decrement particle cooldown each frame
        if (droppodParticleCooldownRef.current > 0) {
          droppodParticleCooldownRef.current -= 1
        }
        if (pod && pod.position && !isDroppodSelectedRef.current && updatedHero.health < effectiveMaxHealth) {
          const distToPod = Math.hypot(
            updatedHero.position.x - pod.position.x,
            updatedHero.position.y - pod.position.y
          )
          if (distToPod <= DROPPOD_HEAL_RADIUS) {
            // Check if hero was attacked recently
            const framesSinceLastDamage = updatedHero.lastDamageFrame
              ? frameCountRef.current - updatedHero.lastDamageFrame
              : DROPPOD_HEAL_COOLDOWN_FRAMES + 1 // Never damaged = can heal

            if (framesSinceLastDamage > DROPPOD_HEAL_COOLDOWN_FRAMES) {
              // Heal 1% of effective max health per second (divide by 60 for per-frame)
              const healPerFrame = effectiveMaxHealth * DROPPOD_HEAL_PERCENT / 60
              droppodHealAccumulatorRef.current += healPerFrame

              // Only apply heal when accumulated at least 1 HP
              if (droppodHealAccumulatorRef.current >= 1) {
                const healAmount = Math.floor(droppodHealAccumulatorRef.current)
                droppodHealAccumulatorRef.current -= healAmount
                const heroPos = { ...updatedHero.position }
                updatedHero = {
                  ...updatedHero,
                  health: Math.min(effectiveMaxHealth, updatedHero.health + healAmount),
                }
                // Spawn green heal particle periodically (not every heal tick)
                if (droppodParticleCooldownRef.current <= 0) {
                  droppodParticleCooldownRef.current = DROPPOD_HEAL_PARTICLE_COOLDOWN
                  setters.setParticleEffects((prev) => [
                    ...prev,
                    {
                      id: generateId("heal-particle"),
                      position: heroPos,
                      size: { width: 32, height: 32 },
                      colors: ["#44ff44", "#66ff66", "#88ff88", "#22dd22"],
                      timestamp: gameNow(),
                    },
                  ])
                }
              }
            }
          }
        }
      }

      // Ranged heroes (ranger) cannot attack while moving
      const canAttackWhileMoving = updatedHero.type !== "ranger"
      const isAllowedToAttack = canAttackWhileMoving || !updatedHero.isMoving

      if (canHeroAttack(updatedHero) && updatedHero.target && isAllowedToAttack) {
        const target = enemyMap.get(updatedHero.target)
        if (target) {
          const baseDamage = calculateHeroDamage(updatedHero) + getEquipmentDamageBonus(updatedInventory)
          const { damage, isCritical } = applyHeroDamageWithPassives(updatedHero, baseDamage, updatedInventory)

          // Ranger uses arc projectiles like archer towers
          const isRanger = updatedHero.type === "ranger"
          const projectile = isRanger
            ? createRangerArcProjectile(
                updatedHero.position,
                target.position,
                damage,
                target.id,
                updatedHero.id,
                isCritical,
                40, // arcHeight
                500, // arcDuration
              )
            : createProjectilePooled(
                updatedHero.position,
                target.position,
                damage,
                8,
                target.id,
                "hero",
                updatedHero.id,
                isCritical,
                updatedHero.type,
              )
          updatedProjectiles.push(projectile)
          updatedHero = performHeroAttack(updatedHero, updatedInventory)

          // Play ranger attack sounds (aim then release)
          if (isRanger) {
            playRangerAttackAimSound()
            setTimeout(() => playRangerAttackReleaseSound(), 150)
          }
        }
      }

      // === HERO IDLE ANIMATION SYSTEM ===
      const now = gameNow()
      const idleThresholdMs = 20000 // 20 seconds idle before relaxed animation
      const periodicIdleIntervalMs = 20000 // 20 seconds between periodic idle animations
      const timeSinceMovement = now - updatedHero.lastMovementTime

      // Only trigger idle animations when not in combat/hurt/death/skill states
      if (
        updatedHero.animationState === "idle" &&
        !updatedHero.isAttacking &&
        !updatedHero.isMoving &&
        updatedHero.hurtTimer <= 0 &&
        !updatedHero.activeSkillAnimation
      ) {
        if (updatedHero.type === "paladin") {
          // Paladin: after the idle threshold, play Idle_2 and hold the last frame (sitting)
          if (timeSinceMovement >= idleThresholdMs && !updatedHero.isPlayingRelaxedIdle) {
            updatedHero = {
              ...updatedHero,
              animationState: "relaxed-idle",
              animationStartTime: now,
              isPlayingRelaxedIdle: true,
            }
          }
        } else if (updatedHero.type === "siegesmith") {
          // Siegesmith has two looping idle sheets - swap between them periodically so
          // the standing pose doesn't read as a single repeating loop forever
          const timeSinceLastIdle = now - updatedHero.lastIdleAnimationTime

          if (timeSinceLastIdle >= periodicIdleIntervalMs) {
            updatedHero = {
              ...updatedHero,
              animationStartTime: now,
              lastIdleAnimationTime: now,
              selectedIdleVariant: updatedHero.selectedIdleVariant === 1 ? 0 : 1,
            }
          }
        }
      }

      // === SKILL ANIMATION COMPLETION ===
      // After skill animations finish, return to idle state
      if (updatedHero.animationState === "skill" && updatedHero.activeSkillAnimation) {
        // Tremor slam has two phases: pre (jump) then main (slam)
        if (updatedHero.activeSkillAnimation === "tremor-slam") {
          const timeSincePhaseStart = now - updatedHero.animationStartTime

          if (updatedHero.skillAnimationPhase === "pre") {
            // Pre-animation (jump): 10 frames * ~100ms + buffer
            const preAnimDuration = 1100
            if (timeSincePhaseStart >= preAnimDuration) {
              // Transition from jump to slam
              updatedHero = {
                ...updatedHero,
                skillAnimationPhase: "main",
                animationStartTime: now,
              }
            }
          } else {
            // Main animation (slam): 8 frames * ~100ms + buffer
            const mainAnimDuration = 900
            if (timeSincePhaseStart >= mainAnimDuration) {
              updatedHero = {
                ...updatedHero,
                animationState: "idle",
                animationStartTime: now,
                activeSkillAnimation: null,
                skillAnimationPhase: undefined,
              }
            }
          }
        } else {
          // Other skills - single animation phase
          const skillAnimDurations: Record<string, number> = {
            "vanguard-rush": 500,   // 4 frames * 6 animSpeed * 16.67ms ≈ 400ms + buffer
            "spectral-blade": 600,  // 5 frames * 100ms + buffer
            "forge-turret": 1100,   // 10 frames * 100ms + buffer (uses Jump animation)
          }

          const duration = skillAnimDurations[updatedHero.activeSkillAnimation] || 600
          const timeSinceSkillStart = now - updatedHero.animationStartTime

          if (timeSinceSkillStart >= duration) {
            updatedHero = {
              ...updatedHero,
              animationState: "idle",
              animationStartTime: now,
              activeSkillAnimation: null,
              skillAnimationPhase: undefined,
            }
          }
        }
      }

      // === HERO DASH ===
      if (updatedHero.isDashing) {
        const { hero: dashedHero, hitEnemyIds } = updateHeroDash(updatedHero, updatedEnemies, 1)
        updatedHero = dashedHero

        // Apply dash damage and slow to hit enemies (with spell power bonus)
        const dashSpellPower = getSpellPowerBonus(updatedInventory)
        const boostedDashDamage = Math.floor(updatedHero.dashDamage * (1 + dashSpellPower))
        for (const enemyId of hitEnemyIds) {
          const idx = updatedEnemies.findIndex((e) => e.id === enemyId)
          if (idx !== -1) {
            playPaladinVanguardRushHitSound()
            let enemy = damageEnemy(updatedEnemies[idx], boostedDashDamage, updatedHero.position)

            // Apply slow effect if the dash has slow values
            if (updatedHero.dashSlowValue > 0 && updatedHero.dashSlowDuration > 0) {
              const slowEffect: StatusEffect = {
                id: generateId("slow"),
                type: "slow",
                value: updatedHero.dashSlowValue,
                duration: updatedHero.dashSlowDuration,
                maxDuration: updatedHero.dashSlowDuration,
              }
              enemy = {
                ...enemy,
                statusEffects: [...(enemy.statusEffects || []), slowEffect],
              }
            }

            updatedEnemies[idx] = enemy
          }
        }
      }

      // === UPDATE ITEM COOLDOWNS ===
      updatedInventory = updateItemCooldowns(updatedInventory, 1)

      // === UPDATE ACTIVE ITEM EFFECTS ===
      const itemEffectResult = updateActiveItemEffects(updatedHero, updatedItemEffects, 1, updatedInventory)
      updatedHero = itemEffectResult.hero
      updatedItemEffects = itemEffectResult.effects

      // === UPDATE LURE EFFECTS (attract enemies) ===
      for (const effect of updatedItemEffects) {
        if (effect.effectType === "lure" && effect.targetPosition) {
          // Attract nearby enemies to the lure position
          const lureRadius = effect.value // radius in pixels
          const lureRadiusSq = lureRadius * lureRadius

          for (let i = 0; i < updatedEnemies.length; i++) {
            const enemy = updatedEnemies[i]
            const dx = enemy.position.x - effect.targetPosition.x
            const dy = enemy.position.y - effect.targetPosition.y
            const distSq = dx * dx + dy * dy

            if (distSq <= lureRadiusSq && distSq > 100) { // Don't move if already very close
              // Move enemy toward lure at reduced speed
              const dist = Math.sqrt(distSq)
              const moveSpeed = 0.5 // Slow pull toward lure
              updatedEnemies[i] = {
                ...enemy,
                position: {
                  x: enemy.position.x - (dx / dist) * moveSpeed,
                  y: enemy.position.y - (dy / dist) * moveSpeed,
                },
              }
            }
          }
        }
      }
    }

    // === UPDATE AOE VISUAL EFFECTS ===
    let updatedAoeEffects = aoeVisualEffectsRef.current
    if (updatedAoeEffects.length > 0) {
      updatedAoeEffects = updatedAoeEffects
        .map((effect) => ({
          ...effect,
          remainingFrames: effect.remainingFrames - 1,
        }))
        .filter((effect) => effect.remainingFrames > 0)
      setters.setAoeVisualEffects(updatedAoeEffects)
    }

    // === PROCESS PENDING AOE WAVES ===
    const updatedPendingWaves = pendingAoeWavesRef.current
    if (updatedPendingWaves.length > 0) {
      const wavesToTrigger: PendingAoeWave[] = []
      const wavesToKeep: PendingAoeWave[] = []

      // Decrement delay and separate triggered waves
      for (const wave of updatedPendingWaves) {
        const updated = { ...wave, delayFrames: wave.delayFrames - 1 }
        if (updated.delayFrames <= 0) {
          wavesToTrigger.push(updated)
        } else {
          wavesToKeep.push(updated)
        }
      }

      // Apply damage from triggered waves (with spell power bonus)
      const waveSpellPower = getSpellPowerBonus(updatedInventory)
      for (const wave of wavesToTrigger) {
        // Apply spell power to wave damage
        const boostedWave = { ...wave, damage: Math.floor(wave.damage * (1 + waveSpellPower)) }
        const affectedEnemies = applyAoeWaveDamage(boostedWave, updatedEnemies)
        // Update enemies with damage from this wave
        for (const affected of affectedEnemies) {
          const idx = updatedEnemies.findIndex((e) => e.id === affected.id)
          if (idx !== -1) {
            updatedEnemies[idx] = affected
          }
        }
      }

      // Update pending waves state
      if (wavesToKeep.length !== updatedPendingWaves.length) {
        setters.setPendingAoeWaves(wavesToKeep)
      }
    }

    // === UPDATE SKILL EFFECTS ===
    // Get spell power bonus once for all skill effects
    const spellPowerBonus = getSpellPowerBonus(updatedInventory)
    updatedSkillEffects = updatedSkillEffects.filter((effect) => {
      if (isSkillEffectExpired(effect)) {
        return false
      }

      const { effect: newEffect, hitEnemyIds } = updateSkillEffect(effect, updatedEnemies, 1)

      // Apply damage to hit enemies (with spell power bonus)
      const skillDamage = Math.floor(effect.damage * (1 + spellPowerBonus))
      for (const enemyId of hitEnemyIds) {
        const idx = updatedEnemies.findIndex((e) => e.id === enemyId)
        if (idx !== -1) {
          updatedEnemies[idx] = damageEnemy(updatedEnemies[idx], skillDamage, effect.position)
        }
      }

      // Update effect in place (via reference reassignment in filter)
      Object.assign(effect, newEffect)
      return true
    })

    // === UPDATE ENTANGLING ROOTS EFFECTS ===
    updatedRootsEffects = updatedRootsEffects.filter((rootEffect) => {
      if (isEntanglingRootsExpired(rootEffect)) {
        return false
      }

      const { effect: newRootEffect, damagedEnemies, newlyRootedEnemyIds } = updateEntanglingRootsEffect(
        rootEffect,
        updatedEnemies,
        1,
      )

      // Apply damage to affected enemies (with spell power bonus)
      for (const { id, damage } of damagedEnemies) {
        const idx = updatedEnemies.findIndex((e) => e.id === id)
        if (idx !== -1) {
          const rootDamage = Math.floor(damage * (1 + spellPowerBonus))
          updatedEnemies[idx] = damageEnemy(updatedEnemies[idx], rootDamage, rootEffect.position)
        }
      }

      // Apply root status to newly caught enemies
      for (const enemyId of newlyRootedEnemyIds) {
        const idx = updatedEnemies.findIndex((e) => e.id === enemyId)
        if (idx !== -1) {
          const enemy = updatedEnemies[idx]
          // Create root status effect with remaining duration
          const rootStatusEffect = {
            id: generateId("root"),
            type: "root" as const,
            value: 0, // Root is binary - enemy can't move
            duration: newRootEffect.rootRemaining,
            maxDuration: newRootEffect.rootRemaining,
          }
          updatedEnemies[idx] = {
            ...enemy,
            statusEffects: [...(enemy.statusEffects || []), rootStatusEffect],
          }
        }
      }

      // Update effect in place
      Object.assign(rootEffect, newRootEffect)
      return true
    })

    // === UPDATE FIRE GROUND EFFECTS ===
    let updatedFireEffects = [...fireEffectsRef.current]
    const fireResult = updateFireGroundEffects(updatedFireEffects, updatedEnemies, 16)
    updatedFireEffects = fireResult.effects

    // Apply fire damage to enemies
    for (const [enemyId, damage] of fireResult.damagedEnemies) {
      const idx = updatedEnemies.findIndex((e) => e.id === enemyId)
      if (idx !== -1) {
        // Find a fire effect position for the damage indicator
        const firePos = updatedFireEffects.length > 0 ? updatedFireEffects[0].position : updatedEnemies[idx].position
        updatedEnemies[idx] = damageEnemy(updatedEnemies[idx], damage, firePos)
      }
    }

    // === UPDATE ENEMY STATUS EFFECTS AND ELITE ABILITIES ===
    updatedEnemies = updatedEnemies.map((enemy) => {
      let updated = updateEnemyStatusEffects(enemy)
      // Update phase shift state
      updated = updatePhaseShift(updated, frameCountRef.current)
      // Update elite ability cooldown
      updated = updateEliteAbilityCooldown(updated)
      // Apply passive regeneration (elite golem)
      updated = applyPassiveRegeneration(updated)
      return updated
    })

    // === PROCESS PERIODIC ELITE ABILITIES ===
    // Track enemies to spawn from Lich resurrection
    const enemiesToSpawn: Enemy[] = []
    // Track towers to disable from Beholder
    const towersToDisable: { towerId: string; duration: number }[] = []
    // Track werewolves spawned from lycanthropy (used later in soldier combat)
    const werewolvesToSpawn: Enemy[] = []

    for (let i = 0; i < updatedEnemies.length; i++) {
      const enemy = updatedEnemies[i]
      if (!hasEliteAbility(enemy) || !enemy.eliteAbility || !isPeriodicAbilityReady(enemy)) continue
      if (enemy.health <= 0) continue

      const ability = enemy.eliteAbility

      // Beholder: Nullify Gaze - disable nearest tower
      if (ability.effect === "disable-tower") {
        let nearestTower: Tower | null = null
        let nearestDistSq = Infinity

        for (const tower of updatedTowers) {
          const dx = tower.position.x - enemy.position.x
          const dy = tower.position.y - enemy.position.y
          const distSq = dx * dx + dy * dy
          if (distSq < nearestDistSq) {
            nearestDistSq = distSq
            nearestTower = tower
          }
        }

        if (nearestTower) {
          towersToDisable.push({
            towerId: nearestTower.id,
            duration: ability.duration ?? 300, // 5 seconds default
          })
          updatedEnemies[i] = resetPeriodicAbilityCooldown(updatedEnemies[i])

          // Create visual effect for disable
          setters.setParticleEffects((prev) => [
            ...prev,
            {
              id: generateId("nullify-gaze"),
              position: { ...nearestTower!.position },
              size: { width: 40, height: 40 },
              colors: ["#FFFF00", "#FFD700", "#FF8C00", "#FF4500"], // Yellow to orange
              timestamp: gameNow(),
              type: "explosion" as const,
            },
          ])
        }
      }

      // Lich: Dark Resurrection - resurrect dead zombies/skeletons
      if (ability.effect === "resurrect-undead") {
        const resurrectionRadius = ability.radius ?? 200
        const maxResurrections = ability.value ?? 2
        let resurrected = 0

        // Find dead zombies/skeletons in corpses
        const corpsesToRemove: string[] = []
        for (const corpse of updatedCorpses) {
          if (resurrected >= maxResurrections) break
          if (corpse.enemyType !== "zombie" && corpse.enemyType !== "skeleton") continue

          const dx = corpse.position.x - enemy.position.x
          const dy = corpse.position.y - enemy.position.y
          const dist = Math.sqrt(dx * dx + dy * dy)

          if (dist <= resurrectionRadius) {
            // Create a resurrected enemy
            const typeString = `${corpse.enemyType}` // Regular variant
            const newEnemy = createEnemy(typeString, corpse.position, enemy.progressionLevel, enemy.nodesCompleted)
            // Mark as resurrected and set to 50% health
            newEnemy.wasResurrected = true
            newEnemy.health = Math.floor(newEnemy.maxHealth * 0.5)
            newEnemy.maxHealth = newEnemy.health

            enemiesToSpawn.push(newEnemy)
            corpsesToRemove.push(corpse.id)
            resurrected++
          }
        }

        if (resurrected > 0) {
          updatedEnemies[i] = resetPeriodicAbilityCooldown(updatedEnemies[i])
          // Remove used corpses
          updatedCorpses = updatedCorpses.filter((c) => !corpsesToRemove.includes(c.id))

          // Create resurrection visual effect
          setters.setParticleEffects((prev) => [
            ...prev,
            {
              id: generateId("dark-resurrection"),
              position: { ...enemy.position },
              size: { width: 60, height: 60 },
              colors: ["#4B0082", "#8B008B", "#9400D3", "#00FF00"], // Purple/green necromantic
              timestamp: gameNow(),
              type: "explosion" as const,
            },
          ])
        }
      }
    }

    // Add resurrected enemies
    if (enemiesToSpawn.length > 0) {
      updatedEnemies = [...updatedEnemies, ...enemiesToSpawn]
    }

    // Apply tower disables (by setting a disabled timer on the tower)
    for (const disable of towersToDisable) {
      const idx = updatedTowers.findIndex((t) => t.id === disable.towerId)
      if (idx !== -1) {
        updatedTowers[idx] = {
          ...updatedTowers[idx],
          disabledUntilFrame: frameCountRef.current + disable.duration,
        }
      }
    }

    // Add werewolves spawned from lycanthropy
    if (werewolvesToSpawn.length > 0) {
      updatedEnemies = [...updatedEnemies, ...werewolvesToSpawn]
    }

    // === UPDATE SUMMONED UNITS ===
    const newSummonProjectiles: Projectile[] = []
    for (let i = 0; i < updatedSummons.length; i++) {
      const summon = updatedSummons[i]

      if (summon.type === "wolf" || summon.type === "fox" || summon.type === "boar") {
        // Update melee summon targeting and movement (wolf, fox, boar all use same logic)
        const { unit: updatedUnit, hitEnemyIds } = updateSummonedWolf(summon, updatedEnemies, 1)
        updatedSummons[i] = updatedUnit

        // Apply damage to hit enemies
        for (const enemyId of hitEnemyIds) {
          const idx = updatedEnemies.findIndex((e) => e.id === enemyId)
          if (idx !== -1) {
            const damage = Math.floor(summon.damage.min + Math.random() * (summon.damage.max - summon.damage.min + 1))
            updatedEnemies[idx] = damageEnemy(updatedEnemies[idx], damage, summon.position)
          }
        }
      } else if (summon.type === "siege-tower") {
        // Update siege tower targeting and laser beam
        const currentLaser = updatedLaserBeams.find(l => l.sourceId === summon.id) || null
        const { unit: updatedTower, laserEffect } = updateSummonedTower(summon, updatedEnemies, currentLaser)
        updatedSummons[i] = updatedTower

        if (laserEffect) {
          // Update or add laser effect
          const existingIdx = updatedLaserBeams.findIndex(l => l.sourceId === summon.id)
          if (existingIdx >= 0) {
            updatedLaserBeams[existingIdx] = laserEffect
          } else {
            // New laser - turret starting to attack
            updatedLaserBeams.push(laserEffect)
            playSiegesmithTurretAttackSound()
          }
        } else {
          // Remove laser if no target
          updatedLaserBeams = updatedLaserBeams.filter(l => l.sourceId !== summon.id)
        }
      }
    }
    updatedProjectiles = [...updatedProjectiles, ...newSummonProjectiles]

    // === PROCESS LASER BEAM DAMAGE TICKS ===
    const laserNow = gameNow()
    for (const laser of updatedLaserBeams) {
      if (!laser.isActive) continue

      // Check if it's time for a damage tick
      if (laserNow - laser.lastTickTime >= laser.tickInterval) {
        laser.lastTickTime = laserNow

        // Find and damage the target enemy
        const targetIdx = updatedEnemies.findIndex(e => e.id === laser.targetId)
        if (targetIdx !== -1 && updatedEnemies[targetIdx].health > 0) {
          updatedEnemies[targetIdx] = damageEnemy(
            updatedEnemies[targetIdx],
            laser.damage,
            laser.sourcePosition
          )
        }
      }
    }

    // Remove lasers targeting dead or missing enemies
    updatedLaserBeams = updatedLaserBeams.filter(laser => {
      const target = updatedEnemies.find(e => e.id === laser.targetId)
      return target && target.health > 0
    })

    // Remove dead summons
    const deadSummons = updatedSummons.filter((summon) => isSummonDead(summon))
    if (deadSummons.length > 0) {
      // Remove lasers from dead turrets
      const deadSummonIds = new Set(deadSummons.map(s => s.id))
      updatedLaserBeams = updatedLaserBeams.filter(laser => !deadSummonIds.has(laser.sourceId))
    }
    updatedSummons = updatedSummons.filter((summon) => !isSummonDead(summon))

    // === GET UPGRADE MODIFIERS (used for soldiers and towers) ===
    const upgradeModifiers = getUpgradeModifiers(upgradeStateRef.current)
    const soldierHpModifier = upgradeModifiers.soldierHp
    // Reinforcements upgrade adds 1 extra soldier (3 total instead of 2)
    const soldiersPerBarracks = 2 + upgradeModifiers.barracksSpawnCount

    // Track alive soldier IDs before updates (for spawn portrait detection)
    const aliveSoldierIdsBefore = new Set(
      updatedBarracksSoldiers.filter(s => !s.isDead).map(s => s.id)
    )

    // === UPDATE BARRACKS SOLDIERS ===
    // First, check if any barracks towers just finished building and need soldiers spawned
    // Also check if existing barracks need more soldiers (e.g., Reinforcements upgrade was just unlocked)
    for (const tower of updatedTowers) {
      if (tower.type === "barracks" && tower.barracksState && tower.animationState === "idle") {
        // Count existing soldiers for this barracks
        const existingSoldiers = updatedBarracksSoldiers.filter(
          (s) => s.barracksTowerId === tower.id
        )
        const currentSoldierCount = existingSoldiers.length

        // Check if we need to spawn soldiers (either new barracks or Reinforcements upgrade unlocked)
        if (currentSoldierCount < soldiersPerBarracks) {
          const newSoldierIds: string[] = [...tower.barracksState.soldierIds]

          // Spawn missing soldiers (starting from current count)
          for (let i = currentSoldierCount; i < soldiersPerBarracks; i++) {
            const soldier = createBarracksSoldier(tower, i, soldierHpModifier)
            updatedBarracksSoldiers.push(soldier)
            newSoldierIds.push(soldier.id)
          }

          // Update tower's barracksState with all soldier IDs
          const towerIdx = updatedTowers.findIndex((t) => t.id === tower.id)
          if (towerIdx !== -1 && updatedTowers[towerIdx].barracksState) {
            updatedTowers[towerIdx] = {
              ...updatedTowers[towerIdx],
              barracksState: {
                ...updatedTowers[towerIdx].barracksState!,
                soldierIds: newSoldierIds,
              },
            }
          }
        }
      }
    }

    // Update alive soldiers - movement, combat, targeting
    for (let i = 0; i < updatedBarracksSoldiers.length; i++) {
      const soldier = updatedBarracksSoldiers[i]
      if (soldier.isDead) continue

      // Find the barracks tower to get rally point
      const barracksTower = updatedTowers.find((t) => t.id === soldier.barracksTowerId)
      if (!barracksTower || !barracksTower.barracksState) continue

      const { soldier: updatedSoldier, hitEnemyId, damage } = updateBarracksSoldier(
        soldier,
        updatedEnemies,
        barracksTower.barracksState.rallyPoint,
        1, // delta
        barracksTower.level,
        frameCountRef.current,
      )
      updatedBarracksSoldiers[i] = updatedSoldier

      // Apply damage to hit enemy
      if (hitEnemyId && damage > 0) {
        const enemyIdx = updatedEnemies.findIndex((e) => e.id === hitEnemyId)
        if (enemyIdx !== -1) {
          const targetEnemy = updatedEnemies[enemyIdx]
          updatedEnemies[enemyIdx] = damageEnemy(
            targetEnemy,
            damage,
            updatedSoldier.position
          )

          // Apply slow-attacker debuff if enemy has it (elite rat's disease carrier)
          const slowEffect = getSlowAttackerEffect(targetEnemy)
          if (slowEffect) {
            updatedBarracksSoldiers[i] = applySoldierAttackSpeedDebuff(
              updatedBarracksSoldiers[i],
              slowEffect.slowPercent,
              slowEffect.duration,
              frameCountRef.current
            )
          }
        }
      }
    }

    // Check for soldier respawns
    updatedBarracksSoldiers = checkSoldierRespawns(updatedBarracksSoldiers, updatedTowers, soldierHpModifier)

    // === DETECT SOLDIER SPAWNS/RESPAWNS FOR PORTRAIT ANIMATION ===
    const newSpawnPortraits: SoldierSpawnPortrait[] = []
    for (const soldier of updatedBarracksSoldiers) {
      // Soldier is alive and wasn't alive before (new spawn or respawn)
      if (!soldier.isDead && !aliveSoldierIdsBefore.has(soldier.id)) {
        const tower = updatedTowers.find(t => t.id === soldier.barracksTowerId)
        if (tower) {
          newSpawnPortraits.push({
            id: generateId("spawn-portrait"),
            soldierId: soldier.id,
            towerId: tower.id,
            towerPosition: tower.position,
            barrackLevel: tower.level,
            startTime: gameNow(),
          })
        }
      }
    }

    // Clean up expired portraits (>3 seconds old) and add new ones
    const PORTRAIT_DURATION = 3000
    const portraitNow = gameNow()
    const updatedPortraits = soldierSpawnPortraitsRef.current
      .filter(p => portraitNow - p.startTime < PORTRAIT_DURATION)
      .concat(newSpawnPortraits)

    // Update soldier debuffs (clear expired ones)
    updatedBarracksSoldiers = updatedBarracksSoldiers.map((soldier) =>
      updateSoldierDebuffs(soldier, frameCountRef.current)
    )

    // === UPDATE TOWERS ===
    // Targeting, the build/upgrade animation timer and the archer/mage/siege
    // unit state all run in a single pass. They used to be five separate .map()
    // passes, and each unit-state updater re-scanned every enemy to find the
    // target that updateTower had already picked - two full O(enemies) scans
    // per tower per frame. Now the target is resolved once and shared.
    const now = gameNow()
    const enemyById = new Map<string, Enemy>()
    for (const enemy of updatedEnemies) enemyById.set(enemy.id, enemy)

    updatedTowers = updatedTowers.map((tower) => {
      const rangeModifier = tower.type === "archer" ? upgradeModifiers.archerRange : 1
      let updated = updateTower(tower, updatedEnemies, 16, rangeModifier)

      // Build/upgrade animation finishes after 1s
      if (updated.animationState === "building" || updated.animationState === "upgrading") {
        if (now - updated.animationStartTime >= 1000) {
          updated = { ...updated, animationState: "idle" as const, animationStartTime: now }
        }
      }

      const target = updated.target ? enemyById.get(updated.target) ?? null : null
      updated = updateArcherState(updated, target, 16)
      updated = updateMageState(updated, target, 16)
      updated = updateSiegeState(updated, target, 16)
      return updated
    })

    // === TOWER FIRING ===

    for (let i = 0; i < updatedTowers.length; i++) {
      const tower = updatedTowers[i]
      // Skip disabled towers (from Beholder's Nullify Gaze ability)
      const isDisabled = tower.disabledUntilFrame && tower.disabledUntilFrame > frameCountRef.current
      if (!isDisabled && canTowerFire(tower)) {
        const target = enemyMap.get(tower.target!)
        if (target) {
          // Apply upgrade modifiers to base damage
          let modifiedDamage = tower.damage
          if (tower.type === "archer") {
            modifiedDamage = Math.floor(tower.damage * upgradeModifiers.archerDamage)
          } else if (tower.type === "mage") {
            modifiedDamage = Math.floor(tower.damage * upgradeModifiers.mageDamage)
          } else if (tower.type === "siege") {
            modifiedDamage = Math.floor(tower.damage * upgradeModifiers.siegeDamage)
          }

          // Apply demon's infernal aura damage reduction if tower is in range
          const auraDamageMult = getInfernalAuraDamageMultiplier(updatedEnemies, tower.position)
          if (auraDamageMult < 1) {
            modifiedDamage = Math.max(1, Math.floor(modifiedDamage * auraDamageMult))
          }

          let projectile
          if (tower.type === "archer") {
            // Archer towers use arc projectiles (arrows)
            projectile = createArcProjectile(
              tower.position,
              target.position,
              modifiedDamage,
              target.id,
              tower.id,
              40, // arc height
              500, // arc duration ms
              tower.attackType, // attackType for damage calculation
            )
            // Trigger archer attack animation and play sound
            updatedTowers[i] = triggerArcherAttack(fireTower(tower))
            playTowerFireSound("archer")
          } else if (tower.type === "mage") {
            // Mage towers use standard projectiles (no auto-hit, can miss)
            projectile = createProjectilePooled(
              tower.position,
              target.position,
              modifiedDamage,
              tower.projectileSpeed,
              target.id,
              "tower",
              tower.id,
              false, // isCritical
              undefined, // heroType
              "mage", // towerType
              tower.attackType, // attackType for damage calculation
            )
            // Trigger mage attack animation and play sound
            updatedTowers[i] = triggerMageAttack(fireTower(tower))
            playTowerFireSound("mage")
          } else if (tower.type === "siege") {
            // Siege towers use arc projectiles (boulders) with smoke trail, no auto-hit
            projectile = createSiegeArcProjectile(
              tower.position,
              target.position,
              modifiedDamage,
              target.id,
              tower.id,
              100, // arc height (bigger arc for catapult boulders)
              800, // arc duration ms (slower than archer)
              tower.attackType, // attackType for damage calculation
            )
            // Trigger siege attack animation and play sound
            updatedTowers[i] = triggerSiegeAttack(fireTower(tower))
            playTowerFireSound("siege")
          } else {
            // Other towers use standard projectiles
            projectile = createProjectilePooled(
              tower.position,
              target.position,
              modifiedDamage,
              tower.projectileSpeed,
              target.id,
              "tower",
              tower.id,
              false, // isCritical
              undefined, // heroType
              tower.type, // towerType
              tower.attackType, // attackType for damage calculation
            )
            updatedTowers[i] = fireTower(tower)
          }
          updatedProjectiles.push(projectile)
        }
      }
    }

    // === ENEMY ATTACKS ===
    for (let i = 0; i < updatedEnemies.length; i++) {
      const enemy = updatedEnemies[i]
      if (shouldEnemyAttack(enemy) && enemy.targetId) {
        if (enemy.attackStyle === "ranged") {
          let targetPos = null
          if (enemy.target === "hero" && updatedHero) {
            targetPos = updatedHero.position
          } else if (enemy.target === "tower") {
            const tower = updatedTowers.find((t) => t.id === enemy.targetId)
            if (tower) targetPos = tower.position
          } else if (enemy.target === "summon") {
            const summon = updatedSummons.find((s) => s.id === enemy.targetId)
            if (summon) targetPos = summon.position
          } else if (enemy.target === "soldier") {
            const soldier = updatedBarracksSoldiers.find((s) => s.id === enemy.targetId && !s.isDead)
            if (soldier) targetPos = soldier.position
          }

          if (targetPos) {
            const attackDamage = calculateEnemyDamage(enemy)
            const enemyConfig = enemyTypes[enemy.type]
            const projectile = createProjectilePooled(
              enemy.position,
              targetPos,
              attackDamage,
              5,
              enemy.targetId,
              "enemy",
              enemy.id,
              false, // isCritical
              undefined, // heroType
              undefined, // towerType
              undefined, // attackType
              enemyConfig?.projectileColor, // customColor
            )
            updatedProjectiles.push(projectile)
          }
        } else if (enemy.attackStyle === "melee") {
          // Calculate damage once for this attack
          const attackDamage = calculateEnemyDamage(enemy)

          // Handle melee attacks on different target types
          if (enemy.target === "hero" && updatedHero) {
            // Apply buff damage reduction first (Empower Armor)
            const buffReduction = getActiveDamageReduction(updatedHero)
            const damageAfterBuff = Math.floor(attackDamage * (1 - buffReduction))
            // Apply armor reduction from equipment
            const equipmentArmor = getEquipmentArmorBonus(updatedInventory)
            const totalArmor = updatedHero.armor + equipmentArmor
            // Simple armor formula: each point of armor reduces damage by 3%
            const armorReduction = Math.min(0.75, totalArmor * 0.03) // Cap at 75% reduction
            const reducedDamage = Math.max(1, Math.floor(damageAfterBuff * (1 - armorReduction)))
            updatedHero = damageHero(updatedHero, reducedDamage, frameCountRef.current)

            // Damage reflect - deal damage back to melee attacker
            const meleeReflectPercent = getDamageReflectPercent(heroInventory)
            if (meleeReflectPercent > 0) {
              const reflectedDamage = Math.floor(reducedDamage * meleeReflectPercent)
              if (reflectedDamage > 0) {
                const enemyIdx = updatedEnemies.findIndex((e) => e.id === enemy.id)
                if (enemyIdx !== -1) {
                  updatedEnemies[enemyIdx] = damageEnemy(updatedEnemies[enemyIdx], reflectedDamage, updatedHero.position)
                }
              }
            }

            // Apply vampire lifesteal if enemy has that ability
            if (hasLifesteal(enemy)) {
              updatedEnemies[i] = applyLifesteal(updatedEnemies[i], reducedDamage)
            }

            // Immediate death check after damage
            if (updatedHero.health <= 0) {
              // Try to restore from clone
              const restoreResult = tryRestoreFromClone(updatedInventory)
              if (restoreResult.success && restoreResult.hero && restoreResult.inventory) {
                updatedHero = restoreResult.hero
                updatedInventory = restoreResult.inventory
                heroRef.current = updatedHero
                heroInventoryRef.current = updatedInventory
                setters.setHero(updatedHero)
                setters.setHeroInventory(updatedInventory)
                setters.triggerCloneRestoreEffect(updatedHero.level, updatedHero.position)
                // Continue game loop with restored hero
              } else {
                heroRef.current = updatedHero
                setters.setHero(updatedHero)
                setters.setBarracksSoldiers([])
                setters.setGameState("defeat")
                playDefeatSound()
                return false
              }
            }

            // Check for heal-on-hit from active buffs (Empower Armor) - only if still alive
            const healResult = checkHealOnHit(updatedHero)
            if (healResult.shouldHeal) {
              const healMaxHealth = getEffectiveMaxHealth(updatedHero, updatedInventory)
              updatedHero = {
                ...updatedHero,
                health: Math.min(healMaxHealth, updatedHero.health + healResult.healAmount),
              }
            }
          } else if (enemy.target === "summon") {
            const summonIdx = updatedSummons.findIndex((s) => s.id === enemy.targetId)
            if (summonIdx !== -1) {
              updatedSummons[summonIdx] = damageSummon(updatedSummons[summonIdx], attackDamage)

              // Apply vampire lifesteal if enemy has that ability
              if (hasLifesteal(enemy)) {
                updatedEnemies[i] = applyLifesteal(updatedEnemies[i], attackDamage)
              }
            }
          } else if (enemy.target === "soldier") {
            const soldierIdx = updatedBarracksSoldiers.findIndex((s) => s.id === enemy.targetId)
            if (soldierIdx !== -1 && !updatedBarracksSoldiers[soldierIdx].isDead) {
              const soldier = updatedBarracksSoldiers[soldierIdx]
              const damagedSoldier = damageBarracksSoldier(soldier, attackDamage)

              // Apply vampire lifesteal if enemy has that ability
              if (hasLifesteal(enemy)) {
                updatedEnemies[i] = applyLifesteal(updatedEnemies[i], attackDamage)
              }

              // Check if soldier died from this attack
              if (damagedSoldier.health <= 0 && !damagedSoldier.isDead) {
                updatedBarracksSoldiers[soldierIdx] = handleSoldierDeath(damagedSoldier)

                // Werewolf lycanthropy - if killed by werewolf, spawn a new werewolf
                if (hasLycanthropy(enemy)) {
                  const newWerewolf = createWerewolfFromSoldier(
                    { ...soldier.position },
                    enemy.progressionLevel,
                    enemy.nodesCompleted
                  )
                  werewolvesToSpawn.push(newWerewolf)

                  // Create transformation visual effect
                  setters.setParticleEffects((prev) => [
                    ...prev,
                    {
                      id: generateId("lycanthropy"),
                      position: { ...soldier.position },
                      size: { width: 40, height: 40 },
                      colors: ["#8B4513", "#A0522D", "#CD853F", "#DEB887"], // Brown tones
                      timestamp: gameNow(),
                      type: "explosion" as const,
                    },
                  ])
                }
              } else {
                updatedBarracksSoldiers[soldierIdx] = damagedSoldier
              }
            }
          } else if (enemy.target === "tower") {
            const towerIdx = updatedTowers.findIndex((t) => t.id === enemy.targetId)
            if (towerIdx !== -1) {
              // Apply tower damage multiplier (default 0.5 = half damage to towers)
              const towerDamage = Math.floor(attackDamage * enemy.towerDamageMultiplier)
              updatedTowers[towerIdx] = damageTower(updatedTowers[towerIdx], towerDamage)
              playBuildingHitSound()

              // Check if tower was destroyed
              if (isTowerDestroyed(updatedTowers[towerIdx])) {
                const destroyedTower = updatedTowers[towerIdx]

                // Create dust cloud particle effect
                setters.setParticleEffects((prev) => [
                  ...prev,
                  {
                    id: generateId("tower-collapse"),
                    position: { ...destroyedTower.position },
                    size: { width: 60, height: 60 },
                    colors: ["#8B7355", "#A0522D", "#D2B48C", "#DEB887"],
                    timestamp: gameNow(),
                    type: "tower-collapse" as const,
                  },
                ])

                // If barracks, kill all associated soldiers
                if (destroyedTower.type === "barracks") {
                  updatedBarracksSoldiers = updatedBarracksSoldiers.map((soldier) => {
                    if (soldier.barracksTowerId === destroyedTower.id) {
                      return handleSoldierDeath(soldier)
                    }
                    return soldier
                  })
                }

                // Remove the tower
                updatedTowers.splice(towerIdx, 1)
              }
            }
          }
        }

        // Use the current array element, not the loop-start snapshot - reflect
        // damage / lifesteal may have replaced updatedEnemies[i] above
        updatedEnemies[i] = performEnemyAttack(updatedEnemies[i])
      }
    }

    // Remove dead summons after enemy attacks
    updatedSummons = updatedSummons.filter((summon) => !isSummonDead(summon))

    // === UPDATE PROJECTILES ===
    for (let i = 0; i < updatedProjectiles.length; i++) {
      const proj = updatedProjectiles[i]
      if (proj.isArc) {
        // Arc projectiles use time-based update (16ms assumed frame time)
        updateArcProjectileInPlace(proj, 16)
      } else {
        // Standard projectiles use frame-based update
        updateProjectileInPlace(proj, 1)
      }
      // Update siege projectile smoke trail
      if (proj.towerType === "siege") {
        updateSiegeProjectileTrail(proj, 16)
      }
    }

    // === CHECK PROJECTILE HITS ===
    const survivingProjectiles: Projectile[] = []
    for (let i = 0; i < updatedProjectiles.length; i++) {
      const proj = updatedProjectiles[i]

      // Arc projectiles when arc completes
      if (proj.isArc && proj.arcProgress !== undefined && proj.arcProgress >= 1) {
        if (proj.towerType === "siege") {
          // Siege projectiles hit where they land and create fire effect (no auto-hit)
          // Check for any enemy at the landing position
          const landingPos = proj.arcTargetPos || proj.position
          const hasExplosiveRounds = (upgradeStateRef.current.purchaseCounts["arcane-b2"] ?? 0) > 0
          // Explosive rounds doubles the impact radius (15 -> 30)
          const impactRadius = hasExplosiveRounds ? 30 : 15
          for (let j = 0; j < updatedEnemies.length; j++) {
            const enemy = updatedEnemies[j]
            if (isEnemyPhaseShifted(enemy)) continue // Skip phase shifted enemies
            const dx = enemy.position.x - landingPos.x
            const dy = enemy.position.y - landingPos.y
            const dist = Math.sqrt(dx * dx + dy * dy)
            if (dist <= impactRadius + enemy.size / 2) {
              // Apply damage type effectiveness for tower projectiles
              let finalDamage = proj.attackType
                ? calculateDamage(proj.damage, proj.attackType, enemy.defenseType, enemy.armor)
                : proj.damage
              // Apply elite damage reduction for siege towers
              finalDamage = applyEliteDamageReduction(updatedEnemies[j], finalDamage, "siege")
              updatedEnemies[j] = damageEnemy(updatedEnemies[j], finalDamage, landingPos)
              // Trigger phase shift if enemy has that ability
              if (hasEliteAbilityEffect(updatedEnemies[j], "phase-shift") && finalDamage > 0) {
                updatedEnemies[j] = triggerPhaseShift(updatedEnemies[j], frameCountRef.current)
              }
            }
          }
          // Create fire ground effect at landing position (explosive rounds doubles radius and damage)
          const fireEffect = createFireGroundEffect(landingPos, proj.damage, proj.sourceId, hasExplosiveRounds)
          updatedFireEffects.push(fireEffect)
        } else {
          // Archer arrows always hit their target when arc completes (guaranteed hit)
          // Look up by id - the object in enemyMap may be stale (replaced by
          // status-effect updates or earlier damage this frame)
          const idx = updatedEnemies.findIndex((e) => e.id === proj.targetId)
          const targetEnemy = idx !== -1 ? updatedEnemies[idx] : undefined
          if (targetEnemy && targetEnemy.health > 0 && !isEnemyPhaseShifted(targetEnemy)) {
            {
              // Apply damage type effectiveness for tower projectiles
              let finalDamage = proj.attackType
                ? calculateDamage(proj.damage, proj.attackType, targetEnemy.defenseType, targetEnemy.armor)
                : proj.damage
              // Apply elite damage reduction (ranger hero arcs count as hero
              // damage, not archer-tower damage)
              finalDamage = applyEliteDamageReduction(updatedEnemies[idx], finalDamage, proj.source === "hero" ? "hero" : "archer")
              updatedEnemies[idx] = damageEnemy(updatedEnemies[idx], finalDamage, proj.position)
              // Trigger phase shift if enemy has that ability
              if (hasEliteAbilityEffect(updatedEnemies[idx], "phase-shift") && finalDamage > 0) {
                updatedEnemies[idx] = triggerPhaseShift(updatedEnemies[idx], frameCountRef.current)
              }
            }
          }
        }
        releaseProjectile(proj)
        continue
      }

      if (isProjectileExpired(proj, WORLD_WIDTH, WORLD_HEIGHT)) {
        releaseProjectile(proj) // Return to pool
        continue
      }

      const hitResult = checkProjectileHitWithGrid(proj, enemyGridRef.current, updatedHero, updatedTowers, updatedSummons)
      if (hitResult.hit && hitResult.targetId) {
        if (hitResult.targetType === "enemy") {
          // Look up by id - enemyMap objects may be stale copies (see above)
          const idx = updatedEnemies.findIndex((e) => e.id === hitResult.targetId)
          const enemy = idx !== -1 ? updatedEnemies[idx] : undefined
          if (enemy) {
            {
              // Mage tower projectiles have AoE damage that scales with travel time
              if (proj.towerType === "mage" && proj.spawnTime && proj.initialAoeRadius) {
                const elapsed = gameNow() - proj.spawnTime
                const maxScaleTime = 2000 // Same as speed/size scaling
                const progress = Math.min(elapsed / maxScaleTime, 1)
                // AoE radius increases by 150% over time (30 -> 75 pixels)
                const aoeRadius = proj.initialAoeRadius * (1 + progress * 1.5)

                // Damage all enemies in the AoE and track which were hit
                const hitEnemyIds = new Set<string>()
                for (let j = 0; j < updatedEnemies.length; j++) {
                  const aoeEnemy = updatedEnemies[j]
                  if (aoeEnemy.health <= 0 || isEnemyPhaseShifted(aoeEnemy)) continue
                  const dx = aoeEnemy.position.x - proj.position.x
                  const dy = aoeEnemy.position.y - proj.position.y
                  const distSq = dx * dx + dy * dy
                  if (distSq <= aoeRadius * aoeRadius) {
                    hitEnemyIds.add(aoeEnemy.id)
                    let aoeDamage = proj.attackType
                      ? calculateDamage(proj.damage, proj.attackType, aoeEnemy.defenseType, aoeEnemy.armor)
                      : proj.damage
                    // Apply elite damage reduction for mage towers
                    aoeDamage = applyEliteDamageReduction(updatedEnemies[j], aoeDamage, "mage")
                    updatedEnemies[j] = damageEnemy(updatedEnemies[j], aoeDamage, proj.position)
                    // Trigger phase shift if enemy has that ability
                    if (hasEliteAbilityEffect(updatedEnemies[j], "phase-shift") && aoeDamage > 0) {
                      updatedEnemies[j] = triggerPhaseShift(updatedEnemies[j], frameCountRef.current)
                    }
                  }
                }

                // Chain Lightning: bounce to nearest enemy not hit by AoE
                if (!proj.hasBounced && (upgradeStateRef.current.purchaseCounts["arcane-a2"] ?? 0) > 0) {
                  let nearestEnemy: typeof updatedEnemies[0] | null = null
                  let nearestDistSq = Infinity

                  for (const potentialTarget of updatedEnemies) {
                    // Skip enemies that were hit by the AoE or are dead
                    if (hitEnemyIds.has(potentialTarget.id) || potentialTarget.health <= 0) continue

                    const dx = potentialTarget.position.x - proj.position.x
                    const dy = potentialTarget.position.y - proj.position.y
                    const distSq = dx * dx + dy * dy

                    // Chain lightning has a max range (e.g., 150 pixels)
                    const chainRange = 150
                    if (distSq < nearestDistSq && distSq <= chainRange * chainRange) {
                      nearestDistSq = distSq
                      nearestEnemy = potentialTarget
                    }
                  }

                  if (nearestEnemy) {
                    // Create a bounce projectile
                    const bounceProj = createProjectilePooled(
                      { x: proj.position.x, y: proj.position.y },
                      { x: nearestEnemy.position.x, y: nearestEnemy.position.y },
                      proj.damage,
                      proj.speed * 1.5, // Bounce projectile is faster
                      nearestEnemy.id,
                      "tower",
                      proj.sourceId,
                      false,
                      undefined,
                      "mage",
                      proj.attackType
                    )
                    bounceProj.hasBounced = true
                    updatedProjectiles.push(bounceProj)
                  }
                }

                // Play tower hit sound once for AoE
                playTowerHitSound("mage")
              } else {
                // Non-mage tower projectiles: single target damage
                // Skip if enemy is phase shifted
                if (isEnemyPhaseShifted(enemy)) {
                  releaseProjectile(proj)
                  continue
                }

                // Apply damage type effectiveness for tower projectiles
                let finalDamage = proj.source === "tower" && proj.attackType
                  ? calculateDamage(proj.damage, proj.attackType, enemy.defenseType, enemy.armor)
                  : proj.damage

                // Apply elite damage reduction based on source
                if (proj.source === "tower" && proj.towerType) {
                  const towerTypeMap: Record<string, "archer" | "mage" | "siege"> = {
                    archer: "archer",
                    mage: "mage",
                    siege: "siege",
                  }
                  const sourceType = towerTypeMap[proj.towerType]
                  if (sourceType) {
                    finalDamage = applyEliteDamageReduction(enemy, finalDamage, sourceType)
                  }
                } else if (proj.source === "hero") {
                  finalDamage = applyEliteDamageReduction(enemy, finalDamage, "hero")
                }

                updatedEnemies[idx] = damageEnemy(updatedEnemies[idx], finalDamage, proj.position)

                // Trigger phase shift if enemy has that ability
                if (hasEliteAbilityEffect(updatedEnemies[idx], "phase-shift") && finalDamage > 0) {
                  updatedEnemies[idx] = triggerPhaseShift(updatedEnemies[idx], frameCountRef.current)
                }

                // Play tower hit sound
                if (proj.source === "tower" && proj.towerType) {
                  playTowerHitSound(proj.towerType)
                }

                // Apply lifesteal for hero projectiles
                if (proj.source === "hero" && updatedHero) {
                  const lifestealPercent = getLifestealPercent(updatedInventory)
                  if (lifestealPercent > 0) {
                    const healAmount = Math.floor(finalDamage * lifestealPercent)
                    if (healAmount > 0) {
                      const maxHealth = getEffectiveMaxHealth(updatedHero, updatedInventory)
                      const newHealth = Math.min(maxHealth, updatedHero.health + healAmount)

                      // Only apply if we actually heal
                      if (newHealth > updatedHero.health) {
                        // Capture positions before updating hero (for use in callback)
                        const heroPos = { x: updatedHero.position.x, y: updatedHero.position.y }
                        const enemyPos = { x: enemy.position.x, y: enemy.position.y }

                        updatedHero = { ...updatedHero, health: newHealth }

                        // Spawn lifesteal particles - drain from enemy and heal on hero
                        setters.setParticleEffects((prev) => [
                          ...prev,
                          {
                            id: generateId("lifesteal-drain"),
                            position: enemyPos,
                            size: { width: 30, height: 30 },
                            colors: ["#dc2626", "#ef4444", "#b91c1c", "#991b1b"],
                            timestamp: gameNow(),
                            type: "lifesteal-drain",
                          },
                          {
                            id: generateId("lifesteal-heal"),
                            position: heroPos,
                            size: { width: 40, height: 40 },
                            colors: ["#dc2626", "#ef4444", "#b91c1c", "#991b1b"],
                            timestamp: gameNow(),
                            type: "lifesteal-heal",
                            value: healAmount,
                          },
                        ])
                      }
                    }
                  }
                }
              }
            }
          }
        }
        if (hitResult.targetType === "hero" && updatedHero) {
          // Apply buff damage reduction first (Empower Armor)
          const buffReduction = getActiveDamageReduction(updatedHero)
          const damageAfterBuff = Math.floor(proj.damage * (1 - buffReduction))
          // Apply armor reduction from equipment
          const equipmentArmor = getEquipmentArmorBonus(updatedInventory)
          const totalArmor = updatedHero.armor + equipmentArmor
          // Simple armor formula: each point of armor reduces damage by 3%
          const armorReduction = Math.min(0.75, totalArmor * 0.03) // Cap at 75% reduction
          const reducedDamage = Math.max(1, Math.floor(damageAfterBuff * (1 - armorReduction)))
          updatedHero = damageHero(updatedHero, reducedDamage, frameCountRef.current)

          // Damage reflect - deal damage back to projectile source (ranged enemy)
          const projReflectPercent = getDamageReflectPercent(heroInventory)
          if (projReflectPercent > 0 && proj.sourceId) {
            const reflectedDamage = Math.floor(reducedDamage * projReflectPercent)
            if (reflectedDamage > 0) {
              const enemyIdx = updatedEnemies.findIndex((e) => e.id === proj.sourceId)
              if (enemyIdx !== -1) {
                updatedEnemies[enemyIdx] = damageEnemy(updatedEnemies[enemyIdx], reflectedDamage, updatedHero.position)
              }
            }
          }

          // Immediate death check after damage
          if (updatedHero.health <= 0) {
            // Try to restore from clone
            const restoreResult = tryRestoreFromClone(updatedInventory)
            if (restoreResult.success && restoreResult.hero && restoreResult.inventory) {
              updatedHero = restoreResult.hero
              updatedInventory = restoreResult.inventory
              heroRef.current = updatedHero
              heroInventoryRef.current = updatedInventory
              setters.setHero(updatedHero)
              setters.setHeroInventory(updatedInventory)
              setters.triggerCloneRestoreEffect(updatedHero.level, updatedHero.position)
              // Continue game loop with restored hero
            } else {
              heroRef.current = updatedHero
              setters.setHero(updatedHero)
              setters.setBarracksSoldiers([])
              setters.setGameState("defeat")
              playDefeatSound()
              return false
            }
          }

          // Check for heal-on-hit from active buffs (Empower Armor) - only if still alive
          const healResult = checkHealOnHit(updatedHero)
          if (healResult.shouldHeal) {
            const projHealMaxHealth = getEffectiveMaxHealth(updatedHero, updatedInventory)
            updatedHero = {
              ...updatedHero,
              health: Math.min(projHealMaxHealth, updatedHero.health + healResult.healAmount),
            }
          }
        }
        if (hitResult.targetType === "summon") {
          const summonIdx = updatedSummons.findIndex((s) => s.id === hitResult.targetId)
          if (summonIdx !== -1) {
            updatedSummons[summonIdx] = damageSummon(updatedSummons[summonIdx], proj.damage)
          }
        }
        if (hitResult.targetType === "tower") {
          const towerIdx = updatedTowers.findIndex((t) => t.id === hitResult.targetId)
          if (towerIdx !== -1) {
            // Find the enemy that fired this projectile to get their tower damage multiplier
            const sourceEnemy = updatedEnemies.find((e) => e.id === proj.sourceId)
            const towerDamageMultiplier = sourceEnemy?.towerDamageMultiplier ?? 0.5
            const towerDamage = Math.floor(proj.damage * towerDamageMultiplier)
            updatedTowers[towerIdx] = damageTower(updatedTowers[towerIdx], towerDamage)
            playBuildingHitSound()

            // Check if tower was destroyed
            if (isTowerDestroyed(updatedTowers[towerIdx])) {
              const destroyedTower = updatedTowers[towerIdx]

              // Create dust cloud particle effect
              setters.setParticleEffects((prev) => [
                ...prev,
                {
                  id: generateId("tower-collapse"),
                  position: { ...destroyedTower.position },
                  size: { width: 60, height: 60 },
                  colors: ["#8B7355", "#A0522D", "#D2B48C", "#DEB887"],
                  timestamp: gameNow(),
                  type: "tower-collapse" as const,
                },
              ])

              // If barracks, kill all associated soldiers
              if (destroyedTower.type === "barracks") {
                updatedBarracksSoldiers = updatedBarracksSoldiers.map((soldier) => {
                  if (soldier.barracksTowerId === destroyedTower.id) {
                    return handleSoldierDeath(soldier)
                  }
                  return soldier
                })
              }

              // Remove the tower
              updatedTowers.splice(towerIdx, 1)
            }
          }
        }
        releaseProjectile(proj) // Return to pool
        continue
      }

      survivingProjectiles.push(proj)
    }
    updatedProjectiles = survivingProjectiles

    // Remove dead summons after projectile hits
    updatedSummons = updatedSummons.filter((summon) => !isSummonDead(summon))

    // === UPDATE CORPSES (remove expired ones) ===
    updatedCorpses = updatedCorpses.filter((corpse) => {
      const elapsed = now - corpse.createdAt
      return elapsed < corpse.duration
    })

    // === PROCESS DEAD ENEMIES & BASE REACHED ===
    let moneyGained = 0
    let experienceGained = 0
    const goldCoinParticles: { position: Position; reward: number }[] = []
    const lootDrops: { items: DroppedItem[]; itemDefs: { name: string; rarity: string }[] }[] = []

    // Track on-death ability effects to apply
    const onDeathSlowEffects: { position: Position; radius: number; slowPercent: number; duration: number }[] = []
    const onDeathDamageEffects: { position: Position; radius: number; damage: number }[] = []

    updatedEnemies = updatedEnemies.filter((enemy) => {
      if (isEnemyDead(enemy)) {
        moneyGained += enemy.reward
        // Score: +1 for regular, +5 for elite, +50 for unique enemies
        updatedStats.score += enemy.variant === "unique" ? 50 : enemy.variant === "elite" ? 5 : 1
        const config = enemyTypes[enemy.type]
        if (config) experienceGained += config.stats.experienceReward

        // Track position for gold coin particle effect
        if (enemy.reward > 0) {
          goldCoinParticles.push({ position: { ...enemy.position }, reward: enemy.reward })
        }

        // === ELITE ON-DEATH ABILITIES ===
        if (hasEliteAbility(enemy) && enemy.eliteAbility) {
          const ability = enemy.eliteAbility

          // Mushroom: Spore Cloud - slow nearby soldiers/heroes on death
          if (ability.effect === "slow-aoe-death") {
            onDeathSlowEffects.push({
              position: { ...enemy.position },
              radius: ability.radius ?? 80,
              slowPercent: ability.value, // e.g., 0.25 = 25% slow
              duration: ability.duration ?? 240, // 4 seconds
            })
          }

          // Zombie: Toxic Burst - damage nearby soldiers on death
          if (ability.effect === "explode-death") {
            onDeathDamageEffects.push({
              position: { ...enemy.position },
              radius: ability.radius ?? 60,
              damage: ability.value, // e.g., 15 damage
            })
          }
        }

        // Generate loot drops based on enemy variant
        const lootResult = generateEnemyLoot(enemy.variant, { ...enemy.position })
        if (lootResult.items.length > 0) {
          lootDrops.push({
            items: lootResult.items,
            itemDefs: lootResult.itemDefinitions.map(def => ({ name: def.name, rarity: def.rarity })),
          })
        }

        // Create corpse if enemy leaves one
        if (enemy.leavesCorpse) {
          const corpse: Corpse = {
            id: generateId("corpse"),
            position: { ...enemy.position },
            createdAt: gameNow(),
            duration: 20000, // 20 seconds
            enemyType: enemy.type,
            enemyVariant: enemy.variant,
            facingDirection: getCorpseFacingDirection(enemy),
          }
          updatedCorpses.push(corpse)
        }

        return false
      }

      if (hasEnemyReachedBase(enemy)) {
        updatedStats.lives -= 1
        playEnemyFinishLineSound()

        // Elite Goblin: Gold Steal ability
        if (hasGoldSteal(enemy)) {
          const stolenGold = getGoldStealAmount(enemy)
          updatedStats.money = Math.max(0, updatedStats.money - stolenGold)
        }

        return false
      }

      return true
    })

    // === APPLY ON-DEATH ELITE ABILITY EFFECTS ===

    // Collect all on-death particle effects to batch into a single state update
    const onDeathParticles: ParticleEffect[] = []

    // Apply Mushroom spore cloud slow effects to soldiers and hero
    for (const slowEffect of onDeathSlowEffects) {
      const slowEndFrame = frameCountRef.current + slowEffect.duration
      const slowMultiplier = 1 - slowEffect.slowPercent // e.g., 0.75 for 25% slow

      // Slow nearby soldiers
      for (let i = 0; i < updatedBarracksSoldiers.length; i++) {
        const soldier = updatedBarracksSoldiers[i]
        if (soldier.health <= 0 || soldier.isDead) continue
        const dx = soldier.position.x - slowEffect.position.x
        const dy = soldier.position.y - slowEffect.position.y
        const distSq = dx * dx + dy * dy
        const radiusSq = slowEffect.radius * slowEffect.radius
        if (distSq <= radiusSq) {
          // Apply slow effect - use strongest slow if already slowed
          updatedBarracksSoldiers[i] = {
            ...soldier,
            slowMultiplier: Math.min(soldier.slowMultiplier, slowMultiplier),
            slowEndFrame: Math.max(soldier.slowEndFrame, slowEndFrame),
          }
        }
      }
      // Slow hero
      if (updatedHero && updatedHero.health > 0) {
        const dx = updatedHero.position.x - slowEffect.position.x
        const dy = updatedHero.position.y - slowEffect.position.y
        const distSq = dx * dx + dy * dy
        const radiusSq = slowEffect.radius * slowEffect.radius
        if (distSq <= radiusSq) {
          // Apply slow effect - use strongest slow if already slowed
          updatedHero = {
            ...updatedHero,
            slowMultiplier: Math.min(updatedHero.slowMultiplier, slowMultiplier),
            slowEndFrame: Math.max(updatedHero.slowEndFrame, slowEndFrame),
          }
        }
      }
      // Queue spore cloud visual effect
      onDeathParticles.push({
        id: generateId("spore-cloud"),
        position: slowEffect.position,
        size: { width: slowEffect.radius * 2, height: slowEffect.radius * 2 },
        colors: ["#90EE90", "#32CD32", "#228B22", "#006400"], // Green spore colors
        timestamp: gameNow(),
        type: "spore-cloud",
      })
    }

    // Apply Zombie toxic burst damage to nearby soldiers
    for (const damageEffect of onDeathDamageEffects) {
      for (let i = 0; i < updatedBarracksSoldiers.length; i++) {
        const soldier = updatedBarracksSoldiers[i]
        if (soldier.health <= 0 || soldier.isDead) continue
        const dx = soldier.position.x - damageEffect.position.x
        const dy = soldier.position.y - damageEffect.position.y
        const distSq = dx * dx + dy * dy
        const radiusSq = damageEffect.radius * damageEffect.radius
        if (distSq <= radiusSq) {
          updatedBarracksSoldiers[i] = {
            ...soldier,
            health: Math.max(0, soldier.health - damageEffect.damage),
          }
        }
      }
      // Queue toxic burst visual effect
      onDeathParticles.push({
        id: generateId("toxic-burst"),
        position: damageEffect.position,
        size: { width: damageEffect.radius * 2, height: damageEffect.radius * 2 },
        colors: ["#556B2F", "#6B8E23", "#9ACD32", "#ADFF2F"], // Toxic green colors
        timestamp: gameNow(),
        type: "toxic-burst",
      })
    }

    // Batch all on-death particle effects into a single state update
    if (onDeathParticles.length > 0) {
      setters.setParticleEffects((prev) => [...prev, ...onDeathParticles])
    }

    // Apply rewards (with gold bonus from equipment like the Ledger of Fortune)
    if (moneyGained > 0) {
      const goldBonusMultiplier = 1 + getEquipmentGoldBonus(updatedInventory)
      const finalGold = Math.floor(moneyGained * goldBonusMultiplier)
      updatedStats.money += finalGold

      // Create gold coin particle effects for each dead enemy
      if (goldCoinParticles.length > 0) {
        playSingleCoinSound()
        setters.setParticleEffects((prev) => [
          ...prev,
          ...goldCoinParticles.map((gp) => ({
            id: generateId("gold-particle"),
            position: gp.position,
            size: { width: 24, height: 24 },
            colors: ["#FFD700", "#FFA500", "#FFEC8B", "#DAA520"], // Gold colors
            timestamp: gameNow(),
            type: "gold-coin" as const,
            value: Math.floor(gp.reward * goldBonusMultiplier),
          })),
        ])
      }
    }

    if (experienceGained > 0 && updatedHero) {
      const result = grantExperience(updatedHero, experienceGained)
      updatedHero = result.hero

      if (result.leveledUp) {
        playLevelUpSound()
        setters.setParticleEffects((prev) => [
          ...prev,
          {
            id: generateId("level-up"),
            position: { ...updatedHero!.position },
            size: { width: 24, height: 200 },
            colors: ["#FFFFFF", "#F0F8FF"],
            timestamp: gameNow(),
            type: "level-up" as const,
          },
        ])
      }
    }

    // === PROCESS LOOT DROPS (items drop to the ground) ===
    if (lootDrops.length > 0) {
      const allDroppedItems: DroppedItem[] = []
      const allDropMessages: ItemDropMessage[] = []
      const now = gameNow()

      for (const lootDrop of lootDrops) {
        // Add dropped items to ground
        allDroppedItems.push(...lootDrop.items)

        // Create drop messages for each item
        for (let i = 0; i < lootDrop.itemDefs.length; i++) {
          const def = lootDrop.itemDefs[i]
          const item = lootDrop.items[i]
          if (def && item) {
            allDropMessages.push({
              id: generateId("drop-msg"),
              itemName: def.name,
              rarityColor: RARITY_COLORS[def.rarity as keyof typeof RARITY_COLORS] || "#FFFFFF",
              position: item.position,
              startTime: now,
            })

            // Create god ray particle effect at drop location
            setters.setParticleEffects((prev) => [
              ...prev,
              {
                id: generateId("god-ray"),
                position: item.position,
                size: { width: 60, height: 150 },
                colors: ["#FFD700", "#FFEC8B", "#FFFFFF"],
                timestamp: now,
                type: "god-ray" as const,
              },
            ])
          }
        }
      }

      // Play item drop sound if items dropped
      if (allDroppedItems.length > 0) {
        playItemDropSound()
        setters.setDroppedItems((prev) => [...prev, ...allDroppedItems])
        setters.setItemDropMessages((prev) => [...prev, ...allDropMessages])
      }
    }

    // === CLEANUP EXPIRED ITEM DROP MESSAGES ===
    const ITEM_DROP_MESSAGE_DURATION = 3000 // 3 seconds
    setters.setItemDropMessages((prev) =>
      prev.filter((msg) => now - msg.startTime < ITEM_DROP_MESSAGE_DURATION)
    )

    // === CHECK GAME OVER ===
    // More robust death check - treat health < 1 as dead (catches floating point issues)
    if (updatedHero) {
      const heroHealth = Number(updatedHero.health)
      if (heroHealth < 1 || Number.isNaN(heroHealth)) {
        // Try to restore from clone
        const restoreResult = tryRestoreFromClone(updatedInventory)
        if (restoreResult.success && restoreResult.hero && restoreResult.inventory) {
          updatedHero = restoreResult.hero
          updatedInventory = restoreResult.inventory
          heroRef.current = updatedHero
          heroInventoryRef.current = updatedInventory
          setters.setHero(updatedHero)
          setters.setHeroInventory(updatedInventory)
          setters.triggerCloneRestoreEffect(updatedHero.level, updatedHero.position)
          // Continue game loop with restored hero
        } else {
          setters.setBarracksSoldiers([])
          setters.setGameState("defeat")
          playDefeatSound()
          return false
        }
      }
    }

    if (updatedStats.lives <= 0) {
      setters.setBarracksSoldiers([])
      setters.setGameState("defeat")
      playDefeatSound()
      return false
    }

    // === CHECK WAVE COMPLETE ===
    if (isWaveComplete(waveSpawnerRef.current, updatedEnemies.length) && !waveSpawnerRef.current.waveRewardClaimed) {
      updatedStats.money += waveSpawnerRef.current.waveReward
      waveSpawnerRef.current = { ...waveSpawnerRef.current, waveRewardClaimed: true }

      if (currentWaveIndex === level.waves.length - 1) {
        // Last wave complete - set flag (animation is triggered via useEffect in game.tsx)
        setters.setIsLastWaveComplete(true)
        // Don't stop the loop - animation needs to run
      } else {
        setters.setShowNextWaveButton(true)
        setters.setWaveStartTime(null) // Clear wave start time when wave completes
      }
    }

    // === UPDATE STATE ===
    // Update refs immediately to prevent stale data in next frame
    // (useEffect syncs are async and may lag behind requestAnimationFrame)
    enemiesRef.current = updatedEnemies
    towersRef.current = updatedTowers
    heroRef.current = updatedHero
    projectilesRef.current = updatedProjectiles
    skillEffectsRef.current = updatedSkillEffects
    summonedUnitsRef.current = updatedSummons
    rootsEffectsRef.current = updatedRootsEffects
    fireEffectsRef.current = updatedFireEffects
    corpsesRef.current = updatedCorpses
    statsRef.current = updatedStats
    heroInventoryRef.current = updatedInventory
    activeItemEffectsRef.current = updatedItemEffects
    barracksSoldiersRef.current = updatedBarracksSoldiers
    soldierSpawnPortraitsRef.current = updatedPortraits

    // Then trigger React state updates for rendering
    setters.setEnemies(updatedEnemies)
    setters.setTowers(updatedTowers)
    setters.setHero(updatedHero)
    setters.setProjectiles(updatedProjectiles)
    setters.setSkillEffects(updatedSkillEffects)
    setters.setSummonedUnits(updatedSummons)
    setters.setRootsEffects(updatedRootsEffects)
    setters.setFireEffects(updatedFireEffects)
    setters.setCorpses(updatedCorpses)
    setters.setLaserBeamEffects(updatedLaserBeams)
    setters.setStats(updatedStats)
    setters.setHeroInventory(updatedInventory)
    setters.setActiveItemEffects(updatedItemEffects)
    setters.setBarracksSoldiers(updatedBarracksSoldiers)
    setters.setSoldierSpawnPortraits(updatedPortraits)

    return true
  }, [level, waveSpawnerRef, currentWaveIndex, setters])

  useEffect(() => {
    if (gameState !== "playing") return

    // Resume the pausable game clock whenever the loop is actually running
    unfreezeGameClock()

    let isRunning = true
    // Note: frameCountRef is intentionally NOT reset here. It is a monotonic
    // session counter - absolute frame stamps stored on entities (slowEndFrame,
    // disabledUntilFrame, ...) must stay comparable across pause/resume and
    // effect re-runs, otherwise those effects never expire.

    // Fixed-timestep accumulator: game logic is authored for 60 ticks/sec, but
    // RAF fires at the display refresh rate (120 Hz phones, 144 Hz monitors).
    // Run 0..MAX_TICKS logic steps per RAF callback based on real elapsed time
    // so game speed is refresh-rate independent.
    const TICK_MS = 1000 / 60
    const MAX_TICKS_PER_FRAME = 8 // cap catch-up after hitches (and 2x fast-forward headroom)
    let lastTime = performance.now()
    let accumulator = 0

    const loop = (now: number) => {
      if (!isRunning) return

      // Safety: skip game loop if page is hidden (defense-in-depth)
      if (typeof document !== "undefined" && document.hidden) {
        freezeGameClock()
        lastTime = now
        accumulator = 0
        animationFrameRef.current = requestAnimationFrame(loop)
        return
      }
      unfreezeGameClock()

      // Clamp elapsed time so a long stall doesn't fast-forward the game.
      // The clock rate scales tick production for the fast-forward toggle -
      // gameNow() advances at the same rate, keeping wall-clock effects in sync.
      accumulator += Math.min(now - lastTime, TICK_MS * MAX_TICKS_PER_FRAME) * getGameClockRate()
      lastTime = now

      let shouldContinue = true
      while (accumulator >= TICK_MS && shouldContinue && isRunning) {
        shouldContinue = runGameLoop()
        accumulator -= TICK_MS
      }

      if (shouldContinue && isRunning) {
        animationFrameRef.current = requestAnimationFrame(loop)
      }
    }

    animationFrameRef.current = requestAnimationFrame(loop)

    return () => {
      isRunning = false
      // Freeze gameplay wall-clock time while the loop is stopped (pause,
      // menus, game over) so Date-based effects don't fast-forward
      freezeGameClock()
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [gameState, runGameLoop])
}
